import { useCallback, useMemo, type ReactNode } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Dash } from "@/components/Dash";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AttachmentGrid, AttachmentTile } from "@/components/AttachmentTile";
import { Markdown } from "@/components/Markdown";
import { inlineImageIds } from "@/utils/inlineImages";
import { useInlineImagePreview } from "@/hooks/useInlineImagePreview";
import { useFileViewer } from "@/components/FileViewerDialog";
import { isPreviewableFile } from "@/utils/files";
import { formatDateTime } from "@/utils/dates";
import {
  conversations as conversationsApi,
  type ConversationMessage,
  type MessageAttachment,
} from "@/features/conversations/api/conversations.api";

const SPINE =
  "relative before:absolute before:left-4 before:top-9 before:bottom-0 before:w-px before:bg-slate-200";

export function MessageCard({
  message,
  internal,
  compact = false,
  spine = false,
  editing,
  actions,
  children,
}: {
  message: ConversationMessage;
  internal: boolean;
  compact?: boolean;
  spine?: boolean;
  editing?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div data-message-id={message.id} className={cn("flex gap-3", spine && SPINE)}>
        <CommentAvatar
          name={message.authorName}
          className={cn("mt-0.5 shrink-0", compact ? "h-7 w-7" : "h-8 w-8")}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words text-sm font-semibold text-slate-900">
              {message.authorName || <Dash />}
            </span>
            {internal && (
              <Badge variant="warning" className="px-1.5 py-0">Internal note</Badge>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-slate-500">
            {formatDateTime(message.createdAt)}
            <EditedLabel message={message} />
          </div>
          <div className="mt-2">{editing ?? <MessageBody message={message} />}</div>
          {actions}
        </div>
      </div>
      {children && <div className={compact ? "pl-10" : "pl-11"}>{children}</div>}
    </div>
  );
}

function EditedLabel({ message }: { message: ConversationMessage }) {
  if (!message.editedAt && message.editCount < 1) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger className="underline decoration-dotted underline-offset-2">
          (edited)
        </TooltipTrigger>
        <TooltipContent className="max-w-56">
          {message.editedAt ? `Edited ${formatDateTime(message.editedAt)}` : "Edited"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function MessageBody({ message }: { message: ConversationMessage }) {
  const viewFile = useFileViewer();
  const urlById = useMemo(() => imageUrls(message), [message]);
  const idByUrl = useMemo(
    () => new Map(Array.from(urlById, ([id, url]) => [url, id])),
    [urlById],
  );
  const loadAttachment = useCallback(
    (aid: string) => conversationsApi.attachmentFile(aid),
    [],
  );
  const previewInlineImage = useInlineImagePreview(idByUrl, loadAttachment);

  if (message.isDeleted) {
    return <p className="text-sm italic text-slate-400">This message was deleted.</p>;
  }

  function view(a: MessageAttachment) {
    viewFile({
      name: a.fileName,
      load: () => conversationsApi.attachmentFile(a.id),
      errorMessage: "Could not open the attachment.",
    });
  }

  async function download(a: MessageAttachment) {
    try {
      await conversationsApi.downloadAttachment(a.id, a.fileName);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download failed.");
    }
  }

  const inlined = new Set(inlineImageIds(message.body));
  const filed = message.attachments.filter((a) => !inlined.has(a.id));

  return (
    <>
      {message.body &&
        (message.bodyFormat === "Markdown" ? (
          <Markdown
            source={message.body}
            imageUrls={urlById}
            onImageClick={previewInlineImage}
          />
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm text-slate-700">{message.body}</p>
        ))}
      {filed.length > 0 && (
        <div className={cn(message.body && "mt-3")}>
          <AttachmentGrid>
            {filed.map((a) => (
              <AttachmentTile
                key={a.id}
                filename={a.fileName}
                contentType={a.contentType}
                sizeBytes={a.sizeBytes}
                onView={isPreviewableFile(a.fileName, a.contentType) ? () => view(a) : undefined}
                onDownload={() => download(a)}
              />
            ))}
          </AttachmentGrid>
        </div>
      )}
    </>
  );
}

const AVATAR_TONES = [
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
  "bg-emerald-100 text-emerald-700",
  "bg-violet-100 text-violet-700",
  "bg-amber-100 text-amber-700",
  "bg-teal-100 text-teal-700",
];

export function CommentAvatar({
  name,
  className,
}: {
  name: string | null | undefined;
  className?: string;
}) {
  return (
    <Avatar className={cn("h-6 w-6", className)}>
      <AvatarFallback className={cn("text-xs font-semibold", avatarTone(name))}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

function avatarTone(name: string | null | undefined) {
  const key = (name ?? "").trim().toLowerCase();
  if (!key) return "bg-slate-200 text-slate-600";
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 1000003;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

function initials(name: string | null | undefined) {
  return (
    (name ?? "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function imageUrls(message: ConversationMessage): Map<string, string> {
  const map = new Map<string, string>();
  for (const a of message.attachments) {
    if (a.url) map.set(a.id, a.url);
  }
  return map;
}
